/**
 * @fileoverview Pinia store for developer settings
 * @module stores/developer
 *
 * Uses centralized defaults from brandingDefaults.ts for consistency.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/plugins/firebase'
import { useUIStore } from '@/stores/ui'
import type { DeveloperBrandConfig, DeveloperSettings, LogEntry } from '@/types/developer'
import {
  COLOR_DEFAULTS,
  FONT_DEFAULTS,
  IDENTITY_DEFAULTS,
  ADMIN_DEFAULTS,
  ADMIN_COLOR_DEFAULTS,
  SOCIAL_DEFAULTS,
  PATH_DEFAULTS,
  STORAGE_DEFAULTS,
  COLOR_PRESETS,
  TIMING_DEFAULTS,
  LOGO_SIZE_DEFAULTS,
} from '@/config/brandingDefaults'

export const useDeveloperStore = defineStore('developer', () => {
  const uiStore = useUIStore()

  // State
  const brandConfig = ref<DeveloperBrandConfig | null>(null)
  const settings = ref<DeveloperSettings>({
    debugMode: false,
    verboseLogging: false,
    mockApiResponses: false,
    slowNetworkSimulation: false
  })
  const logs = ref<LogEntry[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Listeners
  let unsubBranding: (() => void) | null = null

  // Computed
  const hasChanges = computed(() => {
    // Will be implemented when we add original state tracking
    return false
  })

  /**
   * Indicates when branding is ready (loaded or defaulted)
   * Used by App.vue to gate rendering and prevent FOUC
   */
  const isReady = computed(() => !loading.value && brandConfig.value !== null)

  /**
   * Get default brand configuration from centralized defaults
   */
  function getDefaultBrandConfig(): DeveloperBrandConfig {
    return {
      // Company identity
      identity: { ...IDENTITY_DEFAULTS },

      // Colors from centralized defaults
      colors: {
        primary: COLOR_DEFAULTS.primary,
        primaryDarken1: COLOR_DEFAULTS.primaryDarken1,
        primaryDarken2: COLOR_DEFAULTS.primaryDarken2,
        primaryLighten1: COLOR_DEFAULTS.primaryLighten1,
        secondary: COLOR_DEFAULTS.secondary,
        secondaryDarken1: COLOR_DEFAULTS.secondaryDarken1,
        secondaryLighten1: COLOR_DEFAULTS.secondaryLighten1,
        accent: COLOR_DEFAULTS.accent,
        info: COLOR_DEFAULTS.info,
        success: COLOR_DEFAULTS.success,
        warning: COLOR_DEFAULTS.warning,
        error: COLOR_DEFAULTS.error,
        background: COLOR_DEFAULTS.background,
        surface: COLOR_DEFAULTS.surface,
        surfaceBright: COLOR_DEFAULTS.surfaceBright,
        surfaceLight: COLOR_DEFAULTS.surfaceLight,
        surfaceVariant: COLOR_DEFAULTS.surfaceVariant,
        onPrimary: COLOR_DEFAULTS.onPrimary,
        onSecondary: COLOR_DEFAULTS.onSecondary,
        onBackground: COLOR_DEFAULTS.onBackground,
        onSurface: COLOR_DEFAULTS.onSurface,
        onSurfaceVariant: COLOR_DEFAULTS.onSurfaceVariant,
        textSecondary: COLOR_DEFAULTS.textSecondary,
        footerBackground: COLOR_DEFAULTS.footerBackground,
        footerOnBackground: COLOR_DEFAULTS.footerOnBackground,
      },

      // Admin-specific colors
      adminColors: { ...ADMIN_COLOR_DEFAULTS },

      // Logo URLs (empty by default, populated from Firebase Storage)
      logos: {
        // Main logos
        light: '',
        dark: '',
        // Favicons
        favicon: '',
        favicon16: '',
        favicon32: '',
        favicon48: '',
        favicon180: '',
        favicon192: '',
        // Apple
        appleTouchIcon: '',
        // Android/PWA
        androidChrome192: '',
        androidChrome512: '',
        // Safari/macOS
        maskIcon: '',
        // Logo variants
        logo16: '',
        logo32: '',
        logo64: '',
        logo128: '',
        logo256: '',
        logo512: '',
        logo1024: '',
        logo2048: '',
        logo4096: '',
        logoMaster: '',
        // Social media
        ogImage: '',
        twitterCard: '',
        socialSquare: '',
        // Email & documents
        emailHeader: '',
        emailFooter: '',
        watermark: '',
        signature: '',
        // App stores
        appStoreIcon: '',
        playStoreIcon: '',
        playStoreFeature: '',
        // Microsoft/Windows
        msTile70: '',
        msTile150: '',
        msTile310: '',
        msTile310x150: '',
        // Splash screens
        splashLight: '',
        splashDark: ''
      },

      // Fonts from centralized defaults
      fonts: {
        heading: FONT_DEFAULTS.heading,
        body: FONT_DEFAULTS.body,
        mono: FONT_DEFAULTS.mono,
        fallback: FONT_DEFAULTS.fallback,
      },

      // Admin panel configuration
      admin: { ...ADMIN_DEFAULTS },

      // Social media links
      social: { ...SOCIAL_DEFAULTS },

      // Asset paths
      paths: { ...PATH_DEFAULTS },

      // Storage configuration
      storage: { ...STORAGE_DEFAULTS },

      // Color presets
      presets: [...COLOR_PRESETS],

      // Logo sizing configuration
      logoSizes: { ...LOGO_SIZE_DEFAULTS },

      // Metadata
      updatedAt: null,
      updatedBy: ''
    }
  }

  /**
   * Initialize store and subscribe to Firestore
   */
  async function init() {
    // Prevent re-init if already loaded
    if (brandConfig.value !== null) {
      loading.value = false
      return
    }

    loading.value = true
    error.value = null

    // Timeout fallback - don't hang forever waiting for Firestore
    const loadingTimeout = setTimeout(() => {
      if (loading.value) {
        console.warn('[Developer Store] Firestore timeout - using defaults')
        brandConfig.value = brandConfig.value || getDefaultBrandConfig()
        loading.value = false
      }
    }, 5000)

    try {
      // Subscribe to branding config
      const brandRef = doc(db, 'platform', 'developer')
      unsubBranding = onSnapshot(
        brandRef,
        (snap) => {
          clearTimeout(loadingTimeout)
          if (snap.exists()) {
            const data = snap.data()
            brandConfig.value = {
              ...getDefaultBrandConfig(),
              ...data.branding
            }
          } else {
            // Initialize with defaults
            brandConfig.value = getDefaultBrandConfig()
            console.log('📦 Developer store using defaults (no Firestore doc)')
          }
          loading.value = false
        },
        (err) => {
          clearTimeout(loadingTimeout)
          console.error('Developer settings listener error:', err)
          error.value = 'Failed to load developer settings'
          brandConfig.value = getDefaultBrandConfig()
          loading.value = false
        }
      )
    } catch (err) {
      clearTimeout(loadingTimeout)
      error.value = 'Failed to initialize developer settings'
      brandConfig.value = getDefaultBrandConfig()
      loading.value = false
    }
  }

  /**
   * Save brand configuration to Firestore with timeout
   */
  async function saveBrandConfig(config: Partial<DeveloperBrandConfig>) {
    loading.value = true
    error.value = null

    // Check if Firestore is initialized
    if (!db) {
      error.value = 'Firestore not initialized'
      uiStore.showSnackbar('Firebase not configured properly', 'error')
      loading.value = false
      return
    }

    try {
      const brandRef = doc(db, 'platform', 'developer')

      // Add timeout to prevent infinite hanging (uses centralized timing)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Save timeout - check Firestore rules')), TIMING_DEFAULTS.saveTimeout)
      })

      const savePromise = setDoc(
        brandRef,
        {
          branding: {
            ...brandConfig.value,
            ...config,
            updatedAt: serverTimestamp()
          }
        },
        { merge: true }
      )

      await Promise.race([savePromise, timeoutPromise])

      // Update local state immediately
      brandConfig.value = {
        ...brandConfig.value,
        ...config
      } as DeveloperBrandConfig

      uiStore.showSnackbar('Configuration saved', 'success')
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      const errorMessage = firebaseError?.code === 'permission-denied'
        ? 'Permission denied - check Firestore rules'
        : firebaseError?.message || 'Failed to save configuration'

      error.value = errorMessage
      uiStore.showSnackbar(errorMessage, 'error')
      console.error('Save error:', err)

      // Still update local state for development
      brandConfig.value = {
        ...brandConfig.value,
        ...config
      } as DeveloperBrandConfig
    } finally {
      loading.value = false
    }
  }

  /**
   * Reset brand config to defaults
   */
  async function resetToDefaults() {
    await saveBrandConfig(getDefaultBrandConfig())
  }

  /**
   * Add log entry
   */
  function addLog(entry: Omit<LogEntry, 'timestamp'>) {
    logs.value.push({
      ...entry,
      timestamp: new Date()
    })
    // Keep only last 1000 entries to prevent memory issues
    if (logs.value.length > 1000) {
      logs.value = logs.value.slice(-1000)
    }
  }

  /**
   * Clear logs
   */
  function clearLogs() {
    logs.value = []
  }

  /**
   * Cleanup subscriptions
   */
  function cleanup() {
    unsubBranding?.()
  }

  return {
    // State
    brandConfig,
    settings,
    logs,
    loading,
    error,
    // Computed
    hasChanges,
    isReady,
    // Actions
    init,
    saveBrandConfig,
    resetToDefaults,
    addLog,
    clearLogs,
    cleanup,
    getDefaultBrandConfig
  }
})
